import React, { useState, useEffect } from 'react';
import InputBox from "./InputBox";
import Hints from "./Hints";
import GameOver from "./GameOver";
import jsonData from '../gameState.json'; 
import Answers from './Answers';

export default function Main() {
    const [diseaseName, setDiseaseName] = useState('');
    const [hints, setHints] = useState([]);
    const [hintArray, setHintArray] = useState([]);
    const [allHintsGiven, setAllHintsGiven] = useState(false);
    const [answerArray, setAnswerArray] = useState([
        { name: '', color: 'lightgray', emoji: '' },
        { name: '', color: 'lightgray', emoji: '' },
        { name: '', color: 'lightgray', emoji: '' },
        { name: '', color: 'lightgray', emoji: '' },
        { name: '', color: 'lightgray', emoji: '' },
        { name: '', color: 'lightgray', emoji: '' },
    ]);
    const [currentIndex, setCurrentIndex] = useState(0);
    const [gameState, setGameState] = useState({
        gameNumber: jsonData.gameNumber,
        points: 0,
        maxHints: 0,
        userHints: 0,
        answered: 0,
        isAnswered: false,
        isGameOver: false,
        atEndScreen: false
    });

    useEffect(() => {
        if (!jsonData.selectedDiseases || jsonData.selectedDiseases.length === 0) return;
    
        // Only set the disease name and hints on the first load
        if (diseaseName === '') {
            const initialDisease = jsonData.selectedDiseases[0]?.name;
            setDiseaseName(initialDisease);
    
            const initialHints = jsonData.selectedDiseases[0]?.symptoms || [];
            setHintArray(initialHints);
    
            setGameState(prevState => ({
                ...prevState,
                maxHints: initialHints.length,
                userHints: 1,
            }));
    
            if (initialHints.length > 0) {
                const [firstHint, ...rest] = initialHints;
                setHints([firstHint]);
                setHintArray(rest);
                setAllHintsGiven(false);
            } else {
                setAllHintsGiven(true);
            }
        }
    
        // End game if 6 diseases have been answered
        if (gameState.answered >= 6) {
            setGameState(prevState => ({
                ...prevState,
                isGameOver: true,
                atEndScreen: true
            }));
            setHintArray([])
        }
    }, [diseaseName, jsonData.selectedDiseases, currentIndex, gameState.answered]);


    useEffect(() => {
        if (gameState.isGameOver) {
            window.scrollTo({ top: 0, behavior: 'smooth' });
            setHintArray(["Game Over"])
        }
    }, [gameState.isGameOver]);
    
    

    function addHint() {
        if (hintArray.length > 0 && !gameState.isGameOver) {
            const [newHint, ...rest] = hintArray;
            setHints(prevHints => [...prevHints, newHint]);
            setHintArray(rest);

            setGameState(prevState => ({
                ...prevState,
                userHints: prevState.userHints + 1
            }));

            if (rest.length === 0) {
                setAllHintsGiven(true);
            }
        }
    }

    function giveUp() {
        if (!gameState.isGameOver) {
            setGameState(prevState => ({
                ...prevState,
                answered: prevState.answered + 1
            }));
            addAnswer({ name: diseaseName, color: 'red', emoji: '🟥' });
            getNextDisease();
        }
    }

    function addAnswer(newAnswer) {
        setAnswerArray(prevAnswers => {
            const updatedAnswers = [...prevAnswers];
            const firstEmptyIndex = updatedAnswers.findIndex(answer => answer.name === '');
            if (firstEmptyIndex !== -1) {
                updatedAnswers[firstEmptyIndex] = { name: newAnswer.name, color: newAnswer.color, emoji: newAnswer.emoji };
            }
            return updatedAnswers;
        });

        // Check if the game should end after answering
        if (gameState.answered + 1 >= 6) {
            setGameState(prevState => ({
                ...prevState,
                isGameOver: true,
                atEndScreen:true,
            }));
            setHintArray([])
        }
    }

    function calculatePoints() {
        const { userHints, maxHints } = gameState;
        if (maxHints > 0) {
            const ratio = (userHints - 1) / maxHints;
            let pointsEarned;
            if (ratio > 0.8) {
                pointsEarned = 1;
            } else if (ratio > 0.6) {
                pointsEarned = 2;
            } else if (ratio > 0) {
                pointsEarned = 3;
            } else {
                pointsEarned = 5;
            }
            setGameState(prevState => ({
                ...prevState,
                points: prevState.points + pointsEarned,
                answered: prevState.answered + 1
            }));
        }
    }

    function getNextDisease() {
        if (gameState.isGameOver || !jsonData.selectedDiseases || jsonData.selectedDiseases.length === 0) return;

        const nextIndex = (currentIndex + 1) % jsonData.selectedDiseases.length;
        const newDisease = jsonData.selectedDiseases[nextIndex]?.name;

        if (newDisease) {
            setDiseaseName(newDisease);
            setCurrentIndex(nextIndex);
            const initialHints = jsonData.selectedDiseases.find(entry => entry.name === newDisease)?.symptoms || [];
            setHintArray(initialHints);
            setHints([]);
            setAllHintsGiven(false);
            
            setGameState(prevState => ({
                ...prevState,
                maxHints: initialHints.length,
                userHints: 0,
            }));

            if (initialHints.length > 0) {
                const [firstHint, ...rest] = initialHints;
                setHints([firstHint]);
                setHintArray(rest);
            }

            // Check if the game should end after answering
            if (gameState.answered >= 6) {
                setGameState(prevState => ({
                    ...prevState,
                    isGameOver: true,
                    atEndScreen:true,
                }));
                setHintArray([])
            }
        }
    }

    return (
        <div className="everything">
            <div className="name--component"> 
                <h1>Medi-Guesser #{gameState.gameNumber} 🐧</h1>
            </div>
            {gameState.isGameOver && (
                <div className="thank--you">
                    <h2>Thank you for playing today's Medi-Guesser</h2>
                    <p onClick={() => setGameState(prevState => ({ ...prevState, atEndScreen: true }))}>
                        Share Results
                    </p>
                </div>
            )}
            {gameState.atEndScreen && (
                <div className="overlay">
                    <GameOver gameState={gameState} setGameState={setGameState} answerArray={answerArray} />
                </div>
            )}
            <div className="main--components">
                <Hints giveUp={giveUp} gameState={gameState} addHint={addHint} hints={hints} allHintsGiven={allHintsGiven} getNextDisease={getNextDisease} />
            </div>
            <InputBox gameState={gameState} calculatePoints={calculatePoints} addHint={addHint} diseaseName={diseaseName} addAnswer={addAnswer} getNextDisease={getNextDisease} />
            <Answers answerArray={answerArray} />
        </div>
    );
}
